import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { File01Icon, EyeIcon, CheckmarkCircle02Icon, Download04Icon, PencilEdit01Icon, Loading03Icon } from '@hugeicons/core-free-icons';

export interface OfferDocument {
    id: number;
    file_name_unsigned: string;
    file_name_signed: string | null;
    file_path: string;
    document_type?: string;
}

export interface OfferDetail {
    offer_amount: number;
    offer_status: string;
    instructions: string;
    offer_issued_at: string;
    documents: OfferDocument[];
    default_url: string;
    cover_letter?: string;
}

@Component({
    selector: 'app-case-offer',
    standalone: true,
    imports: [CommonModule, HugeiconsIconComponent],
    templateUrl: './case-offer.component.html',
    styleUrl: './case-offer.component.scss'
})
export class CaseOfferComponent {
    @Input() offer: OfferDetail | null = null;
    @Input() caseId: number | string | null = null;
    @Input() isAttorney = false;
    @Input() allDocumentsSigned = false;

    /** Emits the 0-based doc index (0=Acknowledgment, 1=ATF, 2=HIPAA) when attorney clicks Sign */
    @Output() signDocument = new EventEmitter<number>();
    /** Emitted when the user clicks "Download" on the Cover Letter */
    @Output() downloadCoverLetter = new EventEmitter<void>();
    @Input() coverLetterDownloading = false;
    /** Emits frontend doc index (0=Ack, 1=ATF, 2=HIPAA) when Download is clicked */
    @Output() downloadDocPdf = new EventEmitter<number>();
    @Input() downloadingDocIndex: number | null = null;
    /** Emitted when the user clicks "View" on the Cover Letter */
    @Output() viewCoverLetter = new EventEmitter<void>();
    @Input() coverLetterViewing = false;
    /** Emits frontend doc index (0=Ack, 1=ATF, 2=HIPAA) when View is clicked */
    @Output() viewDocPdf = new EventEmitter<number>();
    @Input() viewingDocIndex: number | null = null;

    activeTab: 'details' | 'unsigned' = 'details';
    fileIcon = File01Icon;
    viewIcon = EyeIcon;
    checkCircleIcon = CheckmarkCircle02Icon;
    downloadIcon = Download04Icon;
    signIcon = PencilEdit01Icon;
    loadingIcon = Loading03Icon;

    /** Maps backend document_type → DocumentsPreviewModal index (0=Ack, 1=ATF, 2=HIPAA) */
    private readonly DOC_TYPE_TO_INDEX: Record<string, number> = {
        'Acknowledgment of Assignment': 0,
        'Assignment of Trust Funds': 1,
        'Assignment of Trust Funds, Obligations & Responsibilities': 1,
        'Hipaa Authorization': 2,
        'HIPAA Authorization': 2,
    };

    getDocIndex(doc: OfferDocument): number {
        return this.DOC_TYPE_TO_INDEX[doc.document_type || ''] ?? 0;
    }

    onSignClick(doc: OfferDocument): void {
        this.signDocument.emit(this.getDocIndex(doc));
    }

    getStatusClass(status: string): string {
        if (!status) return '';
        return status.toLowerCase();
    }

    private readonly EXCLUDED_DOC_TYPES = ['Cover Letter', 'Release of Lien'];

    get allSignableDocuments(): OfferDocument[] {
        return (this.offer?.documents || []).filter(doc =>
            !this.EXCLUDED_DOC_TYPES.includes(doc.document_type || '')
        );
    }

    get signedDocuments(): OfferDocument[] {
        return (this.offer?.documents || []).filter(doc =>
            doc.file_name_signed && !this.EXCLUDED_DOC_TYPES.includes(doc.document_type || '')
        );
    }

    getUnsignedDocumentUrl(doc: OfferDocument): string {
        if (!this.offer?.default_url) return '';
        return `${this.offer.default_url}/${doc.file_path}/${doc.file_name_unsigned}`;
    }

    getSignedDocumentUrl(doc: OfferDocument): string {
        if (!this.offer?.default_url || !doc.file_name_signed) return '';
        return `${this.offer.default_url}/${doc.file_path}/${doc.file_name_signed}`;
    }

    get coverLetterUrl(): string {
        if (!this.offer?.default_url || !this.offer?.cover_letter) return '';
        return `${this.offer.default_url}/${this.offer.cover_letter}`;
    }

    onDownloadDocPdf(doc: OfferDocument): void {
        this.downloadDocPdf.emit(this.getDocIndex(doc));
    }

    onViewDocPdf(doc: OfferDocument): void {
        this.viewDocPdf.emit(this.getDocIndex(doc));
    }
}
